import { useQuery } from '@apollo/client';
import { CardCollectionById } from '@/clients/queries/contentful/CardCollectionById.graphql';
import { centeredMainGridClasses } from '@/styles/grid';
import {
  CardCollectionFieldsFragment,
  CardCollectionByIdQuery,
  CardCollectionByIdQueryVariables,
} from 'types/generated';
import { Heading, Label } from 'components/src/typography';
import Card from '../Card';
import { motion } from 'framer-motion';
import LoadingCardCollection from './LoadingCardCollection';
import clsx from 'clsx';
import { getOr } from '@/utils/options';
import { MobilePackageType } from './Types';
import RichTextContainer from './RichTextContainer';
import { Log } from '@/logging/datadog';

type Props = {
  data: CardCollectionFieldsFragment;
  setMobilePackage: React.Dispatch<React.SetStateAction<MobilePackageType | null>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const renderCarousel = ({ data, setMobilePackage, setShowModal }: Props) => {
  const numberOfCards = data.cardCollection?.items.length || 0;

  return (
    <>
      <div className="col-span-full !col-start-1 mb-32 grid gap-y-4">
        {data?.subtitle && <Label>{data.subtitle}</Label>}
        {data?.title && <Heading variant={'xs'}>{data.title}</Heading>}
        {data?.descriptionMarkdown && (
          <div className="col-span-full whitespace-break-spaces">
            <RichTextContainer data={data.descriptionMarkdown.json} />
          </div>
        )}
      </div>
      {data.cardCollection?.items.map((card) => {
        if (card?.__typename === 'LargeCard') {
          return (
            <div
              key={card.id}
              className={`col-span-full sm:col-span-3 md:col-span-6 [&>div]:h-full ${clsx({
                'lg:col-span-4': numberOfCards === 3,
                'lg:col-span-4 xl:col-span-3': numberOfCards >= 4,
              })}`}
            >
              <Card
                card={card}
                onClick={() => {
                  setShowModal(true);
                  setMobilePackage({
                    subTitle: getOr(data.title, 'Fylla á'),
                    label: getOr(card.title, ''),
                    price: getOr(card.price, 0),
                    id: getOr(card.packageId, 0),
                    selectedFromDropDown: false,
                  });
                }}
                animateOnSuccess={false}
              />
            </div>
          );
        }
        return null;
      })}
    </>
  );
};

const FrelsiCardCollection = ({
  cardCollectionId,
  setMobilePackage,
  setShowModal,
}: {
  cardCollectionId: string;
  setMobilePackage: React.Dispatch<React.SetStateAction<MobilePackageType | null>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { loading, error, data } = useQuery<CardCollectionByIdQuery, CardCollectionByIdQueryVariables>(
    CardCollectionById,
    { variables: { id: cardCollectionId } }
  );

  if (error) {
    Log.error('Error in FrelsiCardCollection', error);
    return <div className={centeredMainGridClasses} />;
  }

  const item = data?.cardCollectionCollection?.items[0] || null;
  const minHeight = '480px';
  const maxHeight = '100%';

  return (
    <motion.div
      className={`${centeredMainGridClasses} gap-24`}
      initial={{ height: minHeight }}
      animate={{ height: loading ? minHeight : maxHeight }}
      transition={{ type: 'tween', ease: 'easeIn', duration: 0.3 }}
    >
      {loading ? <LoadingCardCollection /> : item && renderCarousel({ data: item, setMobilePackage, setShowModal })}
    </motion.div>
  );
};

export default FrelsiCardCollection;
